import { useState } from "react";
import { CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Modal } from "#/components/common/Modal";
import type { NotificationItem } from "../types";

function fullTimestamp(value?: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleString("en-KE", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function timeAgo(value?: string | null): string {
  if (!value) return "";
  const diff = Date.now() - new Date(value).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1)  return "Just now";
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7)  return `${d}d ago`;
  return new Date(value).toLocaleDateString("en-KE", { day: "numeric", month: "short" });
}

function resolveRoute(item: NotificationItem): string | null {
  const id = item.referenceId;
  const t  = (item.referenceType ?? "").toUpperCase();
  if (id) {
    const map: Record<string, string> = {
      LEAVE: "/hr/leave/requests", LEAVE_REQUEST: "/hr/leave/requests",
      // LeaveNotificationService actually dispatches leave-application approval
      // notifications under this exact referenceType (TARGET_LEAVE_APPLICATION) --
      // without this entry the "View related record" link never appeared for a
      // leave approval notification at all.
      LEAVE_APPLICATION: "/hr/leave/requests",
      CLAIM: "/claims",
      TRAINING: "/hr/training", TRAINING_REQUEST: "/hr/training",
      REQUISITION: "/hr/requisitions", HR_REQUISITION: "/hr/requisitions",
      PROCUREMENT: "/procurement/requisitions", PROCUREMENT_RFQ: "/procurement/rfqs",
      ASSET: "/assets", ASSET_DISPOSAL: "/assets/disposals",
      MEMBERSHIP: "/hr/membership/members", MEMBERSHIP_APPLICATION: "/hr/membership/members",
      // MembershipAutomationService's finance/ICT/training "action required" notices
      // (sent to Finance/ICT/Training teams, not the membership officer) use this
      // referenceType -- same gap as LEAVE_APPLICATION above.
      MEMBER_COMPANY: "/hr/membership/members",
      AIRTIME: "/hr/airtime/allocations",
      // InventoryNotificationService's low-stock / reorder alerts use this
      // referenceType, pointing at the specific item that triggered the alert.
      INVENTORY_ITEM: "/inventory/items",
    };
    if (map[t]) return `${map[t]}/${id}`;
  }
  const list: Record<string, string> = {
    OFFICIAL_DUTY: "/hr/leave/official-duty", LEAVE_PLAN: "/hr/leave/plans",
    DOCUMENT: "/documents", ANNOUNCEMENT: "/notifications", NOTIFICATION: "/notifications",
    // Inventory requests are now created/tracked via Staff Requisition (GOODS type)
    INVENTORY: "/hr/requisitions/mine", INVENTORY_REQUEST: "/hr/requisitions/mine",
  };
  return list[t] ?? null;
}

const MOD_COLORS: Record<string, { dot: string; badge: string }> = {
  LEAVE:       { dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700" },
  CLAIM:       { dot: "bg-amber-500",   badge: "bg-amber-50 text-amber-700"   },
  TRAINING:    { dot: "bg-purple-500",  badge: "bg-purple-50 text-purple-700" },
  INVENTORY:   { dot: "bg-teal-500",    badge: "bg-teal-50 text-teal-700"     },
  REQUISITION: { dot: "bg-orange-500",  badge: "bg-orange-50 text-orange-700" },
  PROCUREMENT: { dot: "bg-orange-500",  badge: "bg-orange-50 text-orange-700" },
  ASSET:       { dot: "bg-blue-500",    badge: "bg-blue-50 text-blue-700"     },
  MEMBERSHIP:  { dot: "bg-sky-500",     badge: "bg-sky-50 text-sky-700"       },
  AIRTIME:     { dot: "bg-rose-400",    badge: "bg-rose-50 text-rose-700"     },
};
const DEFAULT_MOD = { dot: "bg-slate-300", badge: "bg-slate-100 text-slate-500" };

type Props = {
  item: NotificationItem;
  rowNumber: number;
  onMarkRead?: (id: number) => void;
  markingRead?: boolean;
};

export function NotificationCard({ item, rowNumber, onMarkRead, markingRead }: Props) {
  const [detailOpen, setDetailOpen] = useState(false);
  const isUnread  = item.status === "UNREAD" || (!item.readAt && item.status !== "READ");
  const route     = resolveRoute(item);
  const modKey    = (item.referenceType ?? "").toUpperCase();
  const modColors = MOD_COLORS[modKey] ?? DEFAULT_MOD;
  const modLabel  = item.referenceType?.replace(/_/g, " ") ?? null;

  // Clicking a row always opens the full notification so it can actually be
  // read (the list only ever shows a truncated one-line preview), and marks
  // it read at the same time. Whether a related record exists is a separate
  // question, offered as a "View" action inside the modal — not the trigger.
  function handleRowClick() {
    if (isUnread && onMarkRead) onMarkRead(item.id);
    setDetailOpen(true);
  }

  const row = (
    <div className={`group relative grid grid-cols-[2.5rem_1fr_7rem_6rem_3rem] items-center gap-3 px-3 py-2.5 transition-colors ${
      isUnread ? "bg-blue-50/50 hover:bg-blue-50" : "bg-white hover:bg-slate-50"
    }`}>

      {/* Unread accent */}
      {isUnread && (
        <span className="pointer-events-none absolute inset-y-0 left-0 w-0.5 rounded-r bg-blue-500" />
      )}

      {/* # Row number */}
      <span className="text-xs text-slate-400 pl-1">{rowNumber}</span>

      {/* Notification title + body */}
      <div className="min-w-0">
        <p className={`truncate text-sm leading-snug ${isUnread ? "font-semibold text-slate-900" : "text-slate-500"}`}>
          {isUnread && (
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-blue-500 align-middle" />
          )}
          {item.title}
        </p>
        {item.message && (
          <p className="mt-0.5 line-clamp-1 text-[11px] text-slate-400">{item.message}</p>
        )}
      </div>

      {/* Module badge */}
      <div>
        {modLabel ? (
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${modColors.badge}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${modColors.dot}`} />
            <span className="truncate">{modLabel}</span>
          </span>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </div>

      {/* Time */}
      <span className="text-[11px] text-slate-400 tabular-nums">
        {timeAgo(item.createdAt)}
      </span>

      {/* Status indicator */}
      <div className="flex justify-center">
        {!isUnread ? (
          <CheckCheck className="h-3.5 w-3.5 text-slate-300" title="Read" />
        ) : (
          <span className="h-2 w-2 rounded-full bg-blue-400" title="Unread" />
        )}
      </div>
    </div>
  );

  return (
    <>
      <button type="button" onClick={handleRowClick} className="block w-full text-left cursor-pointer">
        {row}
      </button>

      <Modal open={detailOpen} title={item.title} onClose={() => setDetailOpen(false)}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {modLabel && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${modColors.badge}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${modColors.dot}`} />
                <span className="truncate">{modLabel}</span>
              </span>
            )}
            <span className="text-xs text-slate-400">{fullTimestamp(item.createdAt)}</span>
          </div>

          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
            {item.message || "No further details were included with this notification."}
          </p>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => setDetailOpen(false)}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              Close
            </button>
            {route && (
              <Link
                to={route}
                onClick={() => setDetailOpen(false)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
              >
                View related record
              </Link>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
